import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Card, CardContent, Checkbox, FormControl, Grid, InputLabel, Select,
    Snackbar, TextField, Typography } from "@mui/material";

import MantenimientoForm from "../../components/common/MantenimientoForm";
import * as catalogoService from "../../services/catalogoService";
import { getsAcceso, getsRol, getsRolAcceso, addUpdateRol, addUpdateRolAccesos } from "../../services/seguridadService";
import { useMantenimiento } from "../../hooks/useMantenimiento";

// Alta y edicion de un rol: sus datos y los accesos que concede. Un acceso
// marcado concede la accion a todos los usuarios del rol.
const ESTADO_ACTIVO = 2;
const ESTADO_INACTIVO = 3;

const Edit = () => {
    const { isReadOnly } = useMantenimiento("rol");
    const params = useParams();
    const navigate = useNavigate();

    const idRol = parseInt(params.id || "0");

    const [entity, setEntity] = useState({
        id: 0,
        nombre: "",
        descripcion: "",
        idModulo: 0,
        idEstado: ESTADO_ACTIVO,
    });

    const [accesos, setAccesos] = useState([]);
    const [accesosDelRol, setAccesosDelRol] = useState([]);
    const [listEstado, setListEstado] = useState([]);

    // Sale de la ruta: al guardar un rol nuevo se navega a su ficha con el
    // mismo componente, y un estado se quedaria en "nuevo".
    const nuevo = idRol == 0;
    const [cargando, setCargando] = useState(true);

    const [openMessage, setOpenMessage] = useState(false);
    const [propsMessage, setPropsMessage] = useState({ severity: "success", message: "" });

    const fetchEntity = useCallback(async () => {
        if (idRol <= 0) {
            setCargando(false);
            return;
        }

        setCargando(true);

        const data = await getsRol().catch((err) => { console.log("Err: ", err) });
        const rol = (data || []).find(x => x.id == idRol);

        if (rol) {
            setEntity({
                id: rol.id,
                nombre: rol.nombre,
                descripcion: rol.descripcion || "",
                idModulo: rol.idModulo || 0,
                idEstado: rol.idEstado,
            });
        }

        const delRol = await getsRolAcceso(idRol).catch((err) => { console.log("Err: ", err) });

        setAccesosDelRol((delRol || []).filter(x => x.idEstado == ESTADO_ACTIVO).map(x => x.idAcceso));
        setCargando(false);
    }, [idRol])

    const fetchAccesos = useCallback(async () => {
        const data = await getsAcceso().catch((err) => { console.log("Err: ", err) });

        setAccesos(data || []);
    }, [])

    const fetchListEstado = useCallback(async () => {
        const data = await catalogoService.getCatalogosByPadre(1)
            .catch((err) => { console.log("Err: ", err) });

        setListEstado(data || []);
    }, [])

    useEffect(() => {
        fetchEntity();
        fetchAccesos();
        fetchListEstado();
    }, [idRol])

    const alternarAcceso = (idAcceso) => {
        setAccesosDelRol((prev) => prev.includes(idAcceso)
            ? prev.filter(x => x != idAcceso)
            : [...prev, idAcceso]);
    }

    const guardar = async () => {
        if (entity.nombre.trim() == "") {
            setPropsMessage({ severity: "error", message: "Indique el nombre del rol." });
            setOpenMessage(true);
            return;
        }

        const id = await addUpdateRol({
            id: entity.id || 0,
            nombre: entity.nombre.trim(),
            descripcion: entity.descripcion == null ? "" : entity.descripcion.trim(),
            idModulo: entity.idModulo || 0,
            idEstado: entity.idEstado || ESTADO_ACTIVO,
        }).catch((err) => { console.log("Err: ", err) });

        if (!id) {
            setPropsMessage({ severity: "error", message: "No se pudo guardar el rol." });
            setOpenMessage(true);
            return;
        }

        // Los accesos marcados conceden y los desmarcados quedan inactivos. Se
        // mandan todos juntos para que no queden accesos a medias.
        await addUpdateRolAccesos(id, accesos.map((acceso) => ({
            idAcceso: acceso.id,
            idEstado: accesosDelRol.includes(acceso.id) ? ESTADO_ACTIVO : ESTADO_INACTIVO,
        }))).catch((err) => { console.log("Err: ", err) });

        setPropsMessage({ severity: "success", message: "Se guardaron los datos del rol con exito." });
        setOpenMessage(true);

        if (nuevo) navigate("/security/role/edit/" + id);
    }

    // Los accesos se agrupan por la pagina (lo que hay antes del punto del
    // codigo) para que la lista sea facil de revisar.
    const porPagina = accesos.reduce((grupos, acceso) => {
        const pagina = (acceso.nombres || "").split(".")[0];

        grupos[pagina] = grupos[pagina] || [];
        grupos[pagina].push(acceso);

        return grupos;
    }, {});

    return (
        <div>
            <MantenimientoForm
                title={(nuevo ? "Nuevo" : "Editar") + " Rol"}
                backTo={"/security/role"}
                onSave={guardar}
                disabled={isReadOnly}
            >
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={12}>
                        <Card>
                            <CardContent sx={{ padding: "0px 10px 10px 10px !important" }}>
                                <Box sx={{ fontWeight: 'bold' }} padding={0}>
                                    <Typography variant="h7">
                                        Datos Básicos
                                    </Typography>
                                </Box>
                                <Grid container spacing={1} alignItems={"center"} padding={"12px 0px 0px 0px"}>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            name="nombre"
                                            label="Nombre"
                                            value={entity.nombre}
                                            fullWidth
                                            disabled={isReadOnly}
                                            onChange={(event) => setEntity({ ...entity, nombre: event.target.value })}
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={5}>
                                        <TextField
                                            name="descripcion"
                                            label="Descripcion"
                                            value={entity.descripcion}
                                            fullWidth
                                            disabled={isReadOnly}
                                            onChange={(event) => setEntity({ ...entity, descripcion: event.target.value })}
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <FormControl fullWidth>
                                            <InputLabel htmlFor="input-estadorol">Estado</InputLabel>
                                            <Select
                                                native
                                                label="Estado"
                                                value={entity.idEstado}
                                                disabled={isReadOnly}
                                                onChange={(event) => setEntity({ ...entity, idEstado: parseInt(event.target.value) })}
                                                inputProps={{ name: 'idEstado', id: 'input-estadorol' }}
                                                size="small"
                                            >
                                                {listEstado.map((row) => (
                                                    <option key={row.id} value={row.id}>{row.descripcion}</option>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={12}>
                        <Card>
                            <CardContent sx={{ padding: "0px 10px 10px 10px !important" }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                    {"Accesos del rol"}
                                </Typography>
                                {Object.keys(porPagina).map((pagina) => (
                                    <Box key={pagina} sx={{ paddingTop: "8px" }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                            {pagina}
                                        </Typography>
                                        {porPagina[pagina].map((acceso) => (
                                            <Box key={acceso.id} sx={{ paddingLeft: "12px" }}>
                                                <Checkbox
                                                    checked={accesosDelRol.includes(acceso.id)}
                                                    disabled={isReadOnly}
                                                    onChange={() => alternarAcceso(acceso.id)}
                                                />
                                                <Typography variant="body2" sx={{ display: 'inline' }}>
                                                    {acceso.descripcion + " (" + acceso.nombres + ")"}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                ))}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </MantenimientoForm>

            <Snackbar open={openMessage} autoHideDuration={5000} onClose={() => setOpenMessage(!openMessage)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert onClose={() => setOpenMessage(!openMessage)} severity={propsMessage.severity}>
                    {propsMessage.message}
                </Alert>
            </Snackbar>
        </div>
    )
}

export default Edit;
